import { useNutritionistConsult } from "@/features/experts/hooks/useNutritionistConsult"
import { useSpecialistConsult } from "@/features/experts/hooks/useSpecialistConsult"
import { useHealthAssessment } from "@/features/health-assessment/hooks/useHealthAssessment"
import { useGetHealthPlan } from "@/features/health-plan/hooks/useGetHealthPlan"
import { useBloodTracker } from "@/features/holifya-tests/blood-test/hooks/useBloodTracker"
import { useDnaTest } from "@/features/holifya-tests/dna-test/hooks/useDnaTest"
import { useDnaTracker } from "@/features/holifya-tests/dna-test/hooks/useDnaTracker"
import { DnaTestTrackingStatus } from "@/features/holifya-tests/dna-test/types/DnaTestTrackingStatus"
import { useGetMealPlans } from "@/features/meal-plan/hooks/useGetMealPlans"
import { useMembership } from "@/features/membership/hooks/useMembership"
import { SubType } from "@/features/membership/types/SubType"
import { useGetDocuments } from "@/features/ocr/hooks/useGetDocuments"
import { BloodTestTrackingStatus } from "@/utils/enums/TrackingStatus"
import { useEffect } from "react"
import { ControlPanelCategory } from "../../types/ControlPanelCategory"
import { ControlPanelSection } from "./ControlPanelSection"

const ControlPanelPageContentArea = () => {
  const { dnaTestUser } = useDnaTest()
  const membership = useMembership()
  const { bloodTracker } = useBloodTracker()
  const { dnaTracker } = useDnaTracker()
  const { isHealthAssessmentCompleted } = useHealthAssessment()
  const { data: documents } = useGetDocuments()
  const { lastNutritionistConsult } = useNutritionistConsult()
  const { lastSpecialistConsult } = useSpecialistConsult()
  const hasAllCommonRequirements = isHealthAssessmentCompleted() && documents && documents.length > 0
  const medicalEquipVisible = (
    (membership === SubType.HEALTH && bloodTracker === BloodTestTrackingStatus.ReportGenerated && hasAllCommonRequirements) ||
    (membership === SubType.HEALTH_DNA && bloodTracker === BloodTestTrackingStatus.ReportGenerated && dnaTracker === DnaTestTrackingStatus.ReportGenerated && hasAllCommonRequirements) ||
    (membership === SubType.NUTRITION && dnaTracker === DnaTestTrackingStatus.ReportGenerated && hasAllCommonRequirements)
  )

  const sectionMap: Record<SubType, ControlPanelCategory[]> = {
    [SubType.HEALTH]: [ControlPanelCategory.BLOOD_TEST, ControlPanelCategory.CLINICAL_HISTORY],
    [SubType.HEALTH_DNA]: [ControlPanelCategory.BLOOD_TEST, ControlPanelCategory.DNA_TEST, ControlPanelCategory.CLINICAL_HISTORY],
    [SubType.NUTRITION]: [ControlPanelCategory.DNA_TEST, ControlPanelCategory.CLINICAL_HISTORY],
    [SubType.METABOLIC]: [ControlPanelCategory.CLINICAL_HISTORY, ControlPanelCategory.MEDICAL_EQUIPE],
    [SubType.METABOLIC_LITE]: [ControlPanelCategory.CLINICAL_HISTORY, ControlPanelCategory.MEDICAL_EQUIPE],
    [SubType.NONE]: [],
    [SubType.OLD]: [],
  }

  const showMedicalEquip = medicalEquipVisible && ![SubType.METABOLIC, SubType.METABOLIC_LITE].includes(membership)
  const showCheckup = isHealthAssessmentCompleted() && (((membership === SubType.HEALTH || membership === SubType.HEALTH_DNA) && lastNutritionistConsult !== undefined) || (membership === SubType.METABOLIC_LITE && lastSpecialistConsult !== undefined) || (membership === SubType.METABOLIC && lastSpecialistConsult !== undefined && lastNutritionistConsult !== undefined))
  const showDna = membership === SubType.NONE && dnaTestUser

  const { healthPlans, refetch } = useGetHealthPlan()
  const { mealPlans, refetch: refetchMealPlans } = useGetMealPlans()

  useEffect(() => {
    refetch()
    refetchMealPlans()
  }, [])

  return (
    <section className="space-y-[20px]">
      {showDna && <ControlPanelSection category={ControlPanelCategory.DNA_TEST} />}
      {healthPlans && healthPlans.length > 0 && <ControlPanelSection category={ControlPanelCategory.HEALTH_PLAN} />}
      {mealPlans && mealPlans.length > 0 && <ControlPanelSection category={ControlPanelCategory.MEAL_PLAN} />}
      {sectionMap[membership]
        .filter(category => !(showCheckup && category === ControlPanelCategory.CLINICAL_HISTORY))
        .map(category => (
          <ControlPanelSection key={category} category={category} />
        ))}
      {showMedicalEquip && <ControlPanelSection category={ControlPanelCategory.MEDICAL_EQUIPE} />}
      {showCheckup && <ControlPanelSection category={ControlPanelCategory.CHECK_UP} />}
    </section>
  )
}

export default ControlPanelPageContentArea
